import React, { useCallback, useState, useEffect } from 'react'
import Checkbox from 'core-system/Checkbox'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import TextField from 'core-system/TextField'
import palette from 'core-system/Themes/palette'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import SegmentService from 'redux/config/services/SegmentService'
import { userActions } from 'redux/user/userSlice'
import { employerActions } from 'redux/employer/employerSlice'
import { Locations } from 'shared/Router/Locations'
import styled from 'styled-components'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import {
  GoogleAuthUrl,
  MsftAuthUrl,
  getClientUrl,
} from 'redux/config/services/Constants'
import GoogleIcon from 'core-system/Icons/Login/Google'
import MicrosoftIcon from 'core-system/Icons/Login/Microsoft'
import OktaIcon from 'core-system/Icons/Login/Okta'
import Divider from 'core-system/Divider'
import TextToggle from 'core-system/TextToggle'
import { ssoActions } from '../../SSO/SSOActions'
import { AppState } from 'redux/config/store'
import { SsoCompany } from '../../SSO/types'
import {
  extractDomainFromEmail,
  findCompanyByEmailDomain,
  buildSsoAuthorizeUrl,
  buildSsoState,
} from '../../SSO/utils'

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const AuthBtnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 2rem;

  @media (min-width: ${(props) => props.theme.breakpoints[2]}) {
    flex-direction: row;
    column-gap: 0.75rem;
  }
`

export const StyledLink = styled(Link)`
  ${(props) => props.theme.typography.action6};
  color: ${(props) => props.theme.palette.primary.pink700};
  text-decoration: underline;
  height: 1.5rem;
  margin-top: 0;
  margin-bottom: 0;
  cursor: pointer;
  text-align: right;
  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
  }
  a {
    color: ${(props) => props.theme.palette.primary.pink700};
  }
`

export const StyledHelperContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 2rem;
  a {
    color: ${(props) => props.theme.palette.primary.pink700};
    flex: 1;
  }
`

const StatusText = styled.p<{ error?: boolean }>`
  margin-top: 1rem;
  ${(props) => props.theme.typography.caption};
  font-size: ${(props) => (props.error ? '0.75rem' : '1rem')};
  color: ${(props) =>
    props.error
      ? props.theme.palette.error.red500
      : props.theme.palette.accent.teal500};
`

const FieldContainer = styled.div`
  margin-bottom: 1.5rem;
`

const StyledAndroidContainer = styled.a`
  margin-left: ${(props) => props.theme.pxToRem(-13)};
  width: unset !important;

  img {
    width: ${(props) => props.theme.pxToRem(168)};
  }
`

const StyledAppleContainer = styled.a`
  width: unset !important;
`

const StyledAppleImage = styled.img`
  border-radius: ${(props) => props.theme.pxToRem(13)};
  width: ${(props) => props.theme.pxToRem(151)};
  padding: 0.625rem;
`

const SuccessMsgContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.success.green700};
  > div {
    color: ${(props) => props.theme.palette.success.green500};
  }
`

interface LoginFormProps {
  userErr: any | null
  loginType: any
  toggleLoginType: (loginType: string) => void
  setPageState: (pageState: string) => void
  qp: any
}

export const LoginForm = React.memo((props: LoginFormProps) => {
  const { loginType, userErr, setPageState, qp, toggleLoginType } = props

  const dispatch = useDispatch()
  const [email, setEmail] = useState<string>()
  const [password, setPassword] = useState<string>()
  const [rememberPass, setRememberPass] = useState<boolean>(true)
  const [isSsoMode, setIsSsoMode] = useState<boolean>(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const { companies, isLoading, error } = useSelector(
    (state: AppState) => state.sso
  )

  const handleSsoLogin = useCallback((company: SsoCompany) => {
    // Construct provider-aware SSO authorize URL.
    // Company ID is encoded in the state parameter, not in the redirect URI.
    const redirectUri = `${getClientUrl()}/login`
    const state = buildSsoState(company.provider || 'okta', company.id)
    const ssoAuthUrl = buildSsoAuthorizeUrl(company, redirectUri, state)

    window.location.href = ssoAuthUrl
    SegmentService.track('sso-company-selected', {
      companyId: company.id,
      companyName: company.name,
      provider: company.provider || 'okta',
    })
  }, [])

  const onSubmit = useCallback(
    (e: React.MouseEvent | React.FormEvent) => {
      e.preventDefault()
      setSsoError(null)

      if (isSsoMode) {
        // SSO login flow
        if (!email) {
          setSsoError('Please enter your email address')
          return
        }

        const emailDomain = extractDomainFromEmail(email)
        if (!emailDomain) {
          setSsoError('Please enter a valid email address')
          return
        }

        const company = findCompanyByEmailDomain(emailDomain, companies || [])
        if (!company) {
          setSsoError(
            'Unable to process SSO login. Please try again or contact support@movewithfleet.com'
          )
          return
        }

        handleSsoLogin(company)
        return
      }

      // Regular login flow
      dispatch(
        userActions.login({
          email,
          password,
          stayLoggedIn: rememberPass,
          platformType: loginType,
        })
      )

      SegmentService.track('fleet-login-click')
    },
    [
      dispatch,
      email,
      password,
      rememberPass,
      loginType,
      isSsoMode,
      companies,
      handleSsoLogin,
    ]
  )

  useEffect(() => {
    if (userErr?.status === false && loginType === 'employer') {
      dispatch(employerActions.getEmployer())
    }
  }, [userErr, loginType])

  // Getting SSO companies
  useEffect(() => {
    dispatch(ssoActions.getSsoCompanies())
  }, [])

  const onChange = useCallback(
    (val: string, key: string) => {
      if (key === 'email') {
        setEmail(val)
        if (isSsoMode) {
          setSsoError(null)
        }
      }
      if (key === 'pwd') {
        setPassword(val)
      }
    },
    [isSsoMode]
  )

  const getSignUpLocation = () => {
    if (loginType === 'employer') {
      return Locations.EmployerSignUp
    } else {
      return Locations.EmployeeSignUp
    }
  }

  const handleSsoClick = useCallback(() => {
    if (isSsoMode) {
      // Toggle off SSO mode
      setIsSsoMode(false)
      setSsoError(null)
    } else {
      setIsSsoMode(true)
      setSsoError(null)
      SegmentService.track('sso-company-select-click', {
        // Mode toggle (company unknown yet). Keep provider=okta for existing Segment boards;
        // ssoMode distinguishes enterprise SSO from social Okta-era naming.
        provider: 'okta',
        ssoMode: true,
      })
    }
  }, [isSsoMode])

  return (
    <>
      {qp.new_signup === 'true' ? (
        <SuccessMsgContainer>
          <Text variant='action2' textAlign='center'>
            {`You've successfully verified your email! Login to complete onboarding.`}
          </Text>
        </SuccessMsgContainer>
      ) : null}
      <Text variant='h1' marginBottom='1.5rem' textColor={palette.black}>
        Login to Fleet
      </Text>
      <Text variant='action2' marginBottom='1.5rem' textColor={palette.black}>
        Welcome back, who are you logging in as?
      </Text>
      <TextToggle
        toggled={loginType === 'employer' ? 0 : 1}
        options={['Admin', 'Commuter']}
        onClick={() => toggleLoginType(loginType)}
        variant='light'
        fullWidth
      />
      <Divider margin='2rem 0' />
      <AuthBtnContainer>
        <SecondaryButton
          onClick={() => {
            window.location.href = GoogleAuthUrl
            SegmentService.track('social-login-click', {
              provider: 'google',
            })
          }}
          startIcon={<GoogleIcon />}
          fullWidth
        >
          Login with Google
        </SecondaryButton>
        <SecondaryButton
          onClick={() => {
            window.location.href = MsftAuthUrl
            SegmentService.track('social-login-click', {
              provider: 'microsoft',
            })
          }}
          startIcon={<MicrosoftIcon />}
          fullWidth
        >
          Login with Microsoft
        </SecondaryButton>
        {/* Not showing the SSO button if loading, there is an error, or no companies are available */}
        {!error && companies && companies.length > 0 && (
          <SecondaryButton
            onClick={handleSsoClick}
            startIcon={!isSsoMode ? <OktaIcon /> : undefined}
            fullWidth
            isLoading={isLoading}
          >
            {isSsoMode ? 'Cancel SSO Login' : 'Login with SSO'}
          </SecondaryButton>
        )}
      </AuthBtnContainer>
      <form onSubmit={onSubmit}>
        <FieldContainer>
          <TextField
            label='Email*'
            placeholder='Email'
            type='email'
            onChange={(e) => onChange(e.currentTarget.value, 'email')}
            onenter={(e) => {
              e.preventDefault()
              onSubmit(e as any)
            }}
          />
        </FieldContainer>
        {!isSsoMode && (
          <FieldContainer>
            <TextField
              label='Password*'
              placeholder='Password'
              type='password'
              onChange={(e) => onChange(e.currentTarget.value, 'pwd')}
              onenter={(e) => {
                e.preventDefault()
                onSubmit(e as any)
              }}
            />
          </FieldContainer>
        )}
        {!isSsoMode && (
          <StyledHelperContainer>
            <Checkbox
              checked={rememberPass}
              onClick={() => setRememberPass(!rememberPass)}
              marginBottom='2rem'
              label='Remember me'
            />
            <StyledLink
              to='#'
              onClick={(e) => {
                e.preventDefault()
                setPageState('forgotPassword')
                SegmentService.track('forgotPassword-link-click')
              }}
            >
              Forgot your password?
            </StyledLink>
          </StyledHelperContainer>
        )}
        {ssoError && <StatusText error>{ssoError}</StatusText>}
        {props.userErr && props.userErr.status && !isSsoMode && (
          <StatusText error>
            {props.userErr?.info?.code === 429
              ? `${props.userErr?.info.statusText}. Please try again or contact support@movewithfleet.com`
              : 'Check your email and/or password. Please try again or contact support@movewithfleet.com.'}
          </StatusText>
        )}
        <ButtonsContainer>
          <PrimaryButton fullWidth onClick={(e) => onSubmit(e)}>
            {isSsoMode ? 'Login with SSO' : 'Login'}
          </PrimaryButton>
        </ButtonsContainer>
        {/* {loginType === 'employee' ? (
          <Text variant='action6'>
            Don&apos;t have an account?{' '}
            <StyledLink
              to={getSignUpLocation()}
              onClick={() =>
                SegmentService.track('signUp-link-click', {
                  platformType: loginType,
                })
              }
            >
              Sign up
            </StyledLink>
          </Text>
        ) : null} */}
        {/* <Text
          variant='body2'
          textColor={palette.text.secondary}
          marginTop='5rem'
        >
          Are you a commuter? Download our mobile app!
        </Text>
        <FlexContainer>
          <StyledAndroidContainer
            href='https://play.google.com/store/apps/details?id=com.fleet_mobile&hl=en_CA&gl=US&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              alt='Get it on Google Play'
              src='https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
            />
          </StyledAndroidContainer>
          <StyledAppleContainer
            href='https://apps.apple.com/us/app/move-with-fleet/id1575305413?itsct=apps_box_badge&amp;itscg=30200'
            target='_blank'
            rel='noopener noreferrer'
          >
            <StyledAppleImage
              src='https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1631232000&h=d5e83c1bb015236d57ef288b4486f8d7'
              alt='Download on the App Store'
            />
          </StyledAppleContainer>
        </FlexContainer> */}
      </form>
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  LoginForm.displayName = 'LoginForm'
}
